import { Trans, useLingui } from "@lingui/react/macro";
import { observable } from "mobx";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import { apiPostClinics } from "../../../api";
import { Button } from "../../../components/ui-kit/button";
import { DialogBody, DialogFooter } from "../../../components/ui-kit/dialog";
import { ChooseField } from "../../../components/ui-kit/fields/choose-field";
import { TextInput } from "../../../components/ui-kit/fields/text-input";
import { Form } from "../../../components/ui-kit/form/form";
import { FormRow } from "../../../components/ui-kit/form/form-row";
import { FormTable } from "../../../components/ui-kit/form/form-table";
import { CountrySelect } from "../../patient/country-select";
import { RegionSelect } from "../../patient/region-select";
import { AdminConsole } from "../admin-console";
import { PhysiciansModal } from "../common/physicians-modal";

/**
 * The new-clinic form on its own, so it can be rendered both as its own dialog
 * and inside a clinic picker that has nothing to pick yet.
 *
 * `compact` drops the physicians row: it opens a picker of its own, and a form
 * living inside a picker must not recurse into another one.
 */
export const AddClinicForm = observer(function AddClinicForm({
  admin,
  onSuccess,
  onCancel,
  compact,
}: {
  admin: AdminConsole;
  onSuccess: (uuid: string) => void;
  onCancel?: () => void;
  compact?: boolean;
}) {
  const { t } = useLingui();
  const [fields] = useState(() =>
    observable({
      name: "",
      ambulance: null as string | null,
      address: "",
      country: "",
      region: null as number | null,
      phone: null as string | null,
      email: null as string | null,
      physicians_uuids: [] as string[],
    }),
  );

  async function save() {
    const response = await apiPostClinics({
      body: {
        ...fields,
        region: fields.region ?? undefined,
      },
    });
    onSuccess(response.data.uuid);
  }

  return (
    <Form className="flex flex-col min-h-0 flex-1" onSubmit={() => save()}>
      <DialogBody>
        <FormTable dataAdd>
          <FormRow
            label={t`Name`}
            data={<TextInput attr="name" model={fields} required />}
            mandatory
          />
          <FormRow
            label={t`Ambulance`}
            data={<TextInput attr="ambulance" model={fields} />}
          />
          <FormRow
            label={t`Address`}
            data={<TextInput attr="address" model={fields} required />}
            mandatory
          />
          <FormRow
            label={t`Country`}
            data={
              <CountrySelect
                attr="country"
                model={fields}
                regionAttr="region"
                required
              />
            }
            mandatory
          />
          <FormRow
            label={t`Region`}
            data={
              <RegionSelect
                attr="region"
                model={fields}
                country={fields.country || undefined}
                required
              />
            }
          />
          <FormRow
            label={t`(General) Phone`}
            data={<TextInput attr="phone" model={fields} />}
          />
          <FormRow
            label={t`(General) Email`}
            data={<TextInput attr="email" model={fields} />}
          />
          {!compact && (
            <FormRow
              label={t`Clinic physicians`}
              data={
                <ChooseField
                  count={fields.physicians_uuids.length}
                  modal={({ onClose }) => {
                    const physicians = admin.createPhysicians();
                    return (
                      <PhysiciansModal
                        title={t`Choose physicians for a new clinic`}
                        preselected={fields.physicians_uuids}
                        filter={{ country: [fields.country] }}
                        mode="multi"
                        model={physicians}
                        onClose={onClose}
                        onConfirm={({ selected }) => {
                          fields.physicians_uuids = selected;
                        }}
                      />
                    );
                  }}
                />
              }
            />
          )}
        </FormTable>
      </DialogBody>

      <DialogFooter>
        {onCancel && (
          <Button type="button" onClick={onCancel}>
            <Trans>Cancel</Trans>
          </Button>
        )}
        <Button variant="primary" type="submit">
          <Trans>Add clinic</Trans>
        </Button>
      </DialogFooter>
    </Form>
  );
});
